<template>
    <v-app>
        <v-container>
            <v-row justify="center" align="center" cols="2" class="mt-3">
                <v-col sm="11" xs="12" md="10" lg="8">
                    <h1 class="blue-grey--text text--darken-3">New Request</h1>
                </v-col>
            </v-row>
            <v-form ref="form" v-model="valid" lazy-validation>
                <v-row justify="center" align="center" id="request-form">
                    <v-col sm="11" xs="12" md="10" lg="8">
                        <v-card elevation="5" class="mb-3 pa-5">
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12">
                                        <v-checkbox
                                                label="Urgent?"
                                                color="error"
                                                hide-details
                                                v-model="form.urgent"
                                                class="mb-7"
                                        ></v-checkbox>
                                        <v-select
                                                :items="websites"
                                                item-value="domain"
                                                item-text="domain"
                                                label="Website"
                                                placeholder="Which website is this for?"
                                                v-model="form.website.domain"
                                                class="mb-2"
                                                :rules="isFilledRule"
                                                @change="updateWebsiteID()"
                                        ></v-select>
                                        <v-text-field
                                                label="Title of Request"
                                                class="mb-2"
                                                placeholder="Enter a title for this request"
                                                v-model="form.title"
                                                :rules="isFilledRule"
                                        ></v-text-field>
                                        <v-dialog
                                                ref="dialog"
                                                :return-value.sync="form.dueDate"
                                                persistent
                                                width="290px"
                                        >
                                            <template v-slot:activator="{ on }">
                                                <v-text-field
                                                        v-model="form.dueDate"
                                                        label="Due Date"
                                                        placeholder="When do you need this request completed by?"
                                                        readonly
                                                        v-on="on"
                                                        class="mb-2"
                                                        required
                                                        :rules="isFilledRule"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker v-model="form.dueDate" scrollable :min="minDateAllowed">
                                                <div class="flex-grow"></div>
                                                <v-spacer></v-spacer>
                                                <v-btn color="primary" @click="$refs.dialog.save(form.dueDate)">OK</v-btn>
                                            </v-date-picker>
                                        </v-dialog>
                                        <div
                                                v-for="(item, index) in form.pages"
                                                :key="index"
                                        >
                                            <h3>
                                                Page #{{ index + 1 }}
                                                <v-btn
                                                        v-if="index > 0"
                                                        class="ml-3"
                                                        color="error"
                                                        outlined
                                                        @click="removePage(index)"
                                                        x-small>Remove</v-btn>
                                            </h3>
                                            <v-text-field
                                                    label="URL"
                                                    placeholder="URL of the page that needs to be updated."
                                                    v-model="item.url"
                                                    class="mt-3 mb-2"
                                                    :rules="isFilledRule"
                                            ></v-text-field>
                                            <v-textarea
                                                    class="mb-2"
                                                    v-model="item.description"
                                                    name="input-7-4"
                                                    label="Description"
                                                    placeholder="Describe what needs to be updated on the page."
                                                    :rules="isFilledRule"
                                            ></v-textarea>
                                        </div>
                                        <v-btn @click="addPage" class="mb-8">Add Page <v-icon right dark>mdi-plus-circle</v-icon></v-btn>
                                        <v-divider></v-divider>
                                        <file-uploads @add-file="addFile" @remove-file="removeFile" v-bind:files="form.files"></file-uploads>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col>
                                        <v-btn
                                                color="primary"
                                                :disabled="!valid"
                                                @click="validate"
                                                :loading="loading"
                                                class="mr-4"
                                        >
                                            Submit
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-form>

            <v-dialog
                    v-model="dialog"
                    max-width="420"
            >
                <v-card>
                    <v-card-title class="headline">Success!</v-card-title>
                    <v-card-text>
                        Request ID: {{ this.ticketID }}
                    </v-card-text>
                    <v-card-text>
                        Your request has been submitted and thanks for using the client portal! Please check your email for confirmation.
                    </v-card-text>

                    <v-card-text align="center">
                        <v-btn @click="this.reload" class="mb-4">Submit Another Task</v-btn>
                        <v-btn to="/" class="primary">Return to Dashboard</v-btn>
                    </v-card-text>
                </v-card>
            </v-dialog>

            <v-dialog
                    v-model="errorDialog"
                    max-width="420"
            >
                <v-card>
                    <v-card-title class="headline">Unable to process request</v-card-title>
                    <v-card-text>
                        We are very sorry for the inconvenience but we are having problems submitting your request.
                        Please try again or at a later time. Our technical team has been notified and will look into the issue.
                    </v-card-text>

                    <v-card-text align="center">
                        <v-btn @click="this.closeErrorDialog" class="mb-4">Close</v-btn><br/>
                        <v-btn to="/" class="primary">Return to Dashboard</v-btn>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-container>
    </v-app>
</template>

<script>
    import { mapState, mapGetters, mapActions } from 'vuex';

    import FileUploads from "./FileUploads";
    import processForm from "../services/redbooth";

    export default {
        name: "NewRequest",
        components: {
            FileUploads
        },
        computed: {
            ...mapGetters(['getUser', 'getAppUserID', 'getOrganization', 'getWebsiteID', 'getWebsites']),
            ...mapState(['user', 'organization']),
            minDate() {
                let today = new Date();
                let futureDate = today.setDate(today.getDate());
                const minDate = new Date(futureDate).toISOString().split('T')[0];
                return minDate;
            }
        },
        async created() {
            await this.fetchUser();
            await this.fetchOrganization();
            this.websites = this.getWebsites;
            this.minDateAllowed = this.minDate;
        },
        data () {
            return {
                loading: false,
                valid: true,
                websites: [],
                dialog: false,
                errorDialog: false,
                ticketID: '',
                minDateAllowed: '',
                confirm: false,
                isFilledRule: [
                    v => !!v || 'This field is required'
                ],
                form: {
                    userID: '',
                    fullName: '',
                    email: '',
                    organization: {
                        id: '',
                        shortName: '',
                        name: '',
                    },
                    title: '',
                    website: {
                        id: '',
                        domain: ''
                    },
                    urgent: false,
                    dueDate: '',
                    pages: [
                        this.newPageObject()
                    ],
                    files: []
                },
            }
        },
        methods: {
            ...mapActions(['fetchUser', 'fetchOrganization', 'fetchRequests']),
            newPageObject() {
                return {
                    url: '',
                    description: ''
                }
            },
            addPage() {
                this.form.pages.push(this.newPageObject());
            },
            removePage(itemNum) {
                this.confirm = false;
                this.form.pages.splice(itemNum, 1);
            },
            addFile(newFile) {
                this.form.files.push(newFile);
            },
            removeFile(file) {
                let itemNum = this.form.files.indexOf(file);
                this.form.files.splice(itemNum, 1);
            },
            reset () {
                this.$refs.form.reset();
                this.form.files = [];

                // Clear out all active pages & start with a fresh new one
                this.form.pages = [];
                this.addPage();
            },
            validate() {
                this.loading = true;
                if (this.$refs.form.validate()) {
                    this.submitForm();
                } else {
                    console.log("Form has errors");
                    this.loading = false;
                }
            },
            async submitForm() {
                this.form.userID = this.getAppUserID;
                this.form.organization.name = this.getOrganization.name;
                this.form.organization.id = this.getOrganization.id;
                this.form.organization.shortName = this.getOrganization.shortName;
                this.form.organization.clientLeads = this.getOrganization.clientLeads;
                this.form.fullName = this.getUser.given_name + " " + this.getUser.family_name;
                this.form.email = this.getUser.email;
                await processForm(this.form)
                    .then(response => {
                        this.ticketID = response.ticketID;
                        this.loading = false;
                        this.dialog = true;
                        this.reset();
                    })
                    .catch(error => {
                        this.loading = false;
                        this.errorDialog = true;
                        console.log(error)
                    })
            },
            updateWebsiteID() {
                this.form.website.id = this.getWebsiteID(this.form.website.domain);
            },
            reload() {
                window.location.reload();
            },
            closeErrorDialog() {
                this.errorDialog = false;
            },
        }
    }
</script>

<style>
    .v-input--selection-controls {
        margin-top: 0px;
        margin-bottom: 35px !important;
    }
    #request-form .v-label {
        color: black ;
        font-weight: bold !important;
        font-size: 18px !important;
    }
    h1, h2, h3 {
        color: black;
    }
</style>